<template>
  <div id="newfeatures">
    <v-container fluid>
      <v-flex xs12>
        <!-- <HowToUseThisPage /> -->
        <div class="jrny-orient-container mb-16">
          <div class="route-container mb-4 section-heading">
            <h1>
              <AppGuideIcon :color="iconColor" class="terms-icon" /> FEATURES
            </h1>
          </div>
          <v-container>
            <v-row class="grid-row">
              <v-card
                class="rounded-ts-xl rounded-be-xl"
                max-width="300"
                v-for="item in visibleFeatures"
                :key="item.id || item.name"
                :cols="item.flex"
              >
                <v-card-text class="pb-0">
                  <v-card-title
                    v-text="item.name"
                    class="pa-0 font-weight-bold"
                  ></v-card-title>
                  <div class="text--primary">
                    {{ item.description }}
                  </div>
                </v-card-text>
                <v-card-text class="pb-0 mt-auto">
                  <!-- <div class="mt-2 mb-0 pb-0">Slug: <span class="grey--text">{{item.slug}}</span></div> -->
                  <div class="mt-2 mb-0 pb-0">
                    <template v-if="isGlobalUserSearchFeature(item)">
                      <span class="text--primary">{{
                        $t('newFeaturesGlobalUserSearch.audienceLine')
                      }}</span>
                    </template>
                    <template v-else>
                      Users:
                      <span class="font-weight-bold">{{
                        item.active ? 'All' : 'Testers'
                      }}</span>
                    </template>
                  </div>
                </v-card-text>
                <v-divider class="mx-4"></v-divider>
                <v-card-actions class="pt-0 mt-0" style="min-height: 32px">
                  <v-checkbox
                    color="#A5D867"
                    style="--v-input-control-height: 32px; min-height: 32px"
                    class="ma-0 pt-0 activate-switch"
                    true-icon="mdi-radiobox-marked"
                    false-icon="mdi-radiobox-blank"
                    :label="!item.active ? 'Deactivated' : 'Live'"
                    hide-details
                    v-model="item.active"
                    :disabled="!item.active && !checkAuthorization(['owner'])"
                    @update:model-value="activateFeature(item)"
                  >
                  </v-checkbox>
                </v-card-actions>
                <v-card-text class="pt-1">
                  <div class="">
                    Slug: <span class="text-grey">{{ item.slug }}</span>
                  </div>
                  <div
                    v-if="isMaintenanceFeature(item)"
                    class="mt-2 d-flex align-center"
                  >
                    <v-btn
                      variant="text"
                      color="#77B718"
                      density="compact"
                      class="px-0 maintenance-settings-btn"
                      @click="openMaintenanceSettings(item)"
                    >
                      Settings
                    </v-btn>
                  </div>
                  <div
                    v-if="isSupportChatFeature(item)"
                    class="mt-2 d-flex align-center"
                  >
                    <v-btn
                      variant="text"
                      color="#77B718"
                      density="compact"
                      class="px-0 maintenance-settings-btn"
                      @click="openSupportChatSettings(item)"
                    >
                      Settings
                    </v-btn>
                  </div>
                  <div
                    v-if="isGlobalUserSearchFeature(item) && isSuperAdminRole()"
                    class="mt-2 d-flex align-center"
                  >
                    <v-btn
                      variant="text"
                      color="#77B718"
                      density="compact"
                      class="px-0 maintenance-settings-btn"
                      @click="openGlobalUserSearchSettings(item)"
                    >
                      Settings
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-row>
          </v-container>
        </div>
      </v-flex>
    </v-container>

    <v-dialog v-model="settingsDialog" max-width="760">
      <v-card>
        <v-card-title class="text-h5 font-weight-bold">
          Maintenance Access Settings
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-4">
            Configure who can access the app when
            <span class="font-weight-bold">maintenance-mode</span> is live.
          </div>

          <v-alert v-if="settingsFeedback" :type="settingsFeedbackType" variant="tonal" class="mb-4">
            {{ settingsFeedback }}
          </v-alert>

          <v-select
            v-model="selectedAllowedRoles"
            :items="roleOptions"
            label="Allowed roles"
            hint="Users with these roles can access while maintenance mode is on."
            persistent-hint
            multiple
            chips
            closable-chips
            clearable
            class="mb-4"
          />

          <v-divider class="my-4" />

          <div class="text-subtitle-1 font-weight-medium mb-2">
            Allowed users (search by email)
          </div>

          <div class="d-flex align-center ga-2">
            <v-text-field
              v-model="emailSearch"
              label="User email"
              variant="outlined"
              density="comfortable"
              hide-details
              class="flex-grow-1"
              @keyup.enter="searchAndAddUser"
            />
            <v-btn
              color="#A5D867"
              class="text-white"
              :loading="isSearchingUser"
              @click="searchAndAddUser"
            >
              Add
            </v-btn>
          </div>

          <div class="text-caption text-grey mt-2">
            Search finds exact email and stores user UID in maintenance config.
          </div>

          <div class="mt-4">
            <v-chip
              v-for="user in allowedUsers"
              :key="user.uid"
              class="ma-1"
              closable
              @click:close="removeAllowedUser(user.uid)"
            >
              {{ user.email || user.uid }}
            </v-chip>
            <div v-if="allowedUsers.length === 0" class="text-grey text-body-2">
              No specific users added.
            </div>
          </div>

          <v-alert type="info" variant="tonal" class="mt-4">
            Owner role always has emergency access.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeMaintenanceSettings">Cancel</v-btn>
          <v-btn
            variant="flat"
            class="maintenance-save-btn"
            :loading="isSavingSettings"
            @click="saveMaintenanceSettings"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="supportSettingsDialog" max-width="820">
      <v-card class="support-settings-dialog-card">
        <v-card-title class="support-settings-dialog-title text-h5 font-weight-bold">
          Support Chat Settings
        </v-card-title>
        <div class="support-settings-dialog-header">
          <div class="text-body-2 mb-4">
            Select the live support provider and manage provider-specific config.
          </div>

          <v-alert
            v-if="supportSettingsFeedback"
            :type="supportSettingsFeedbackType"
            variant="tonal"
            class="mb-4"
          >
            {{ supportSettingsFeedback }}
          </v-alert>

          <v-select
            v-model="supportProvider"
            :items="supportProviderItems"
            item-title="title"
            item-value="value"
            label="Support provider"
            hint="Only one provider can be active at a time."
            persistent-hint
            class="mb-4"
          />

          <v-divider />

          <div class="text-body-2 text-medium-emphasis mb-4">
            All providers can be configured here; only the selection above is
            active in the app. Values are saved together so switching providers
            does not discard prior settings.
          </div>
        </div>

        <div class="support-settings-dialog-scroll pa-6">
          <div class="text-subtitle-1 font-weight-medium mb-2">
            Life on Life Support (ticket form)
          </div>
          <v-text-field
            v-model="lifeOnLifeTicketUrl"
            label="Ticket form URL"
            hint="Used when Support provider is Life on Life Support. Opens in a new tab from the floating support button."
            persistent-hint
            class="mb-2"
          />

          <v-divider class="my-4" />

          <div class="text-subtitle-1 font-weight-medium mb-2">
            OS Ticket / LHC config
          </div>
          <v-text-field
            v-model="lhcBasePath"
            label="LHC base path"
            hint="Defaults to /lhc/lhc_web/index.php/"
            persistent-hint
            class="mb-2"
          />
          <v-text-field
            v-model="lhcWidgetScriptPath"
            label="LHC widget script path"
            hint="Defaults to /lhc/lhc_web/design/defaulttheme/js/widgetv2/index.js"
            persistent-hint
            class="mb-2"
          />
          <v-text-field
            v-model="lhcFallbackStartUrl"
            label="LHC fallback start URL"
            hint="Used when Support provider is LHC — final fallback when the widget cannot open."
            persistent-hint
          />

          <v-divider class="my-4" />

          <div class="text-subtitle-1 font-weight-medium mb-2">
            Zendesk config
          </div>
          <v-text-field
            v-model="zendeskKey"
            label="Zendesk key"
            hint="Used when Support provider is Zendesk — widget key for static.zdassets.com snippet."
            persistent-hint
            class="mb-2"
          />
          <v-text-field
            v-model="zendeskLocale"
            label="Zendesk locale (optional)"
            hint="Example: en-us, pt-br"
            persistent-hint
          />
        </div>

        <v-divider />

        <v-card-actions class="support-settings-dialog-footer">
          <v-spacer />
          <v-btn variant="text" @click="closeSupportChatSettings">Cancel</v-btn>
          <v-btn
            variant="flat"
            class="maintenance-save-btn"
            :loading="isSavingSupportSettings"
            @click="saveSupportChatSettings"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="globalUserSearchSettingsDialog" max-width="560">
      <v-card>
        <v-card-title class="text-h5 font-weight-bold">
          {{ $t('newFeaturesGlobalUserSearch.settingsTitle') }}
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-4">
            {{ $t('newFeaturesGlobalUserSearch.settingsIntro') }}
          </div>

          <v-alert
            v-if="globalUserSearchSettingsFeedback"
            :type="globalUserSearchSettingsFeedbackType"
            variant="tonal"
            class="mb-4"
          >
            {{ globalUserSearchSettingsFeedback }}
          </v-alert>

          <v-switch
            v-model="globalUserSearchHelpDeskAllowed"
            color="#A5D867"
            hide-details
            :label="$t('newFeaturesGlobalUserSearch.helpDeskSwitch')"
          />
          <div class="text-caption text-grey mt-2">
            {{ $t('newFeaturesGlobalUserSearch.helpDeskHint') }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeGlobalUserSearchSettings">
            {{ $t('newFeaturesGlobalUserSearch.cancel') }}
          </v-btn>
          <v-btn
            variant="flat"
            class="maintenance-save-btn"
            :loading="isSavingGlobalUserSearchSettings"
            @click="saveGlobalUserSearchSettings"
          >
            {{ $t('newFeaturesGlobalUserSearch.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import AppGuideIcon from '@/assets/icons/AppGuideIcon.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import { DEFAULT_LIFE_ON_LIFE_TICKET_URL } from '@/constants/lifeOnLifeSupport'

  export default {
    name: 'NewFeatures',
    components: {
      AppGuideIcon,
    },
    data() {
      return {
        feedback: null,
        features: [],
        settingsDialog: false,
        maintenanceFeature: null,
        selectedAllowedRoles: [],
        allowedUsers: [],
        emailSearch: '',
        isSearchingUser: false,
        isSavingSettings: false,
        settingsFeedback: '',
        settingsFeedbackType: 'info',
        roleOptions: ['superAdmin', 'helpDesk', 'admin', 'leader', 'member'],
        supportSettingsDialog: false,
        supportFeature: null,
        supportProvider: 'lhc',
        lifeOnLifeTicketUrl: DEFAULT_LIFE_ON_LIFE_TICKET_URL,
        lhcBasePath: '/lhc/lhc_web/index.php/',
        lhcWidgetScriptPath: '/lhc/lhc_web/design/defaulttheme/js/widgetv2/index.js',
        lhcFallbackStartUrl: 'https://support.lifeonlife.org/lhc/lhc_web/index.php/en/chat/start',
        zendeskKey: '',
        zendeskLocale: '',
        isSavingSupportSettings: false,
        supportSettingsFeedback: '',
        supportSettingsFeedbackType: 'info',
        globalUserSearchSettingsDialog: false,
        globalUserSearchFeature: null,
        globalUserSearchHelpDeskAllowed: true,
        globalUserSearchSettingsFeedback: '',
        globalUserSearchSettingsFeedbackType: 'info',
        isSavingGlobalUserSearchSettings: false,
      }
    },
    computed: {
      supportProviderItems() {
        return [
          { title: 'None', value: 'none' },
          { title: 'Zendesk', value: 'zendesk' },
          {
            title: 'OS Ticket / Live Helper Chat (embedded widget)',
            value: 'lhc',
          },
          {
            title: 'Life on Life Support (ticket form in new tab)',
            value: 'lifeOnLifeSupport',
          },
        ]
      },
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      currentUser() {
        return this.$store.getters['UserData/getUser'] || null
      },
      visibleFeatures() {
        const locale = this.$i18n?.locale || undefined
        return this.features
          .filter((item) => {
            if (this.isGlobalUserSearchFeature(item)) {
              return this.isSuperAdminRole()
            }
            return true
          })
          .sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), locale, {
              sensitivity: 'base',
            })
          )
      },
    },
    methods: {
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const hasRole = permissions.filter(() =>
            userRoles.some((role) => permissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      activateFeature(item) {
        db.collection('newfeatures')
          .doc(item.id)
          .update({
            active: item.active,
            activated: Date.now(),
          })
          .catch((err) => {
            console.log(err)
          })
      },
      isMaintenanceFeature(item) {
        return item && item.slug === 'maintenance-mode'
      },
      isSupportChatFeature(item) {
        return item && item.slug === 'support-chat'
      },
      isGlobalUserSearchFeature(item) {
        return item && item.slug === 'global-user-search'
      },
      isSuperAdminRole() {
        const roles = this.$store.getters['UserData/getRoles'] || []
        return Array.isArray(roles) && roles.includes('superAdmin')
      },
      async openGlobalUserSearchSettings(item) {
        this.globalUserSearchFeature = item
        this.globalUserSearchSettingsDialog = true
        this.globalUserSearchSettingsFeedback = ''
        await this.loadGlobalUserSearchSettings()
      },
      closeGlobalUserSearchSettings() {
        this.globalUserSearchSettingsDialog = false
      },
      async loadGlobalUserSearchSettings() {
        try {
          const doc = await db.collection('config').doc('globalUserSearch').get()
          const d = doc.exists ? doc.data() || {} : {}
          this.globalUserSearchHelpDeskAllowed = d.helpDeskAllowed !== false
        } catch (error) {
          this.globalUserSearchSettingsFeedbackType = 'error'
          this.globalUserSearchSettingsFeedback =
            this.$t('newFeaturesGlobalUserSearch.loadError')
          console.log(error)
        }
      },
      async saveGlobalUserSearchSettings() {
        this.globalUserSearchSettingsFeedback = ''
        this.isSavingGlobalUserSearchSettings = true
        try {
          const updatedBy =
            this.currentUser?.uid || this.currentUser?.email || 'unknown'
          await db
            .collection('config')
            .doc('globalUserSearch')
            .set(
              {
                helpDeskAllowed: this.globalUserSearchHelpDeskAllowed === true,
                updatedAt: Date.now(),
                updatedBy,
              },
              { merge: true }
            )
          this.globalUserSearchSettingsFeedbackType = 'success'
          this.globalUserSearchSettingsFeedback =
            this.$t('newFeaturesGlobalUserSearch.saved')
        } catch (error) {
          this.globalUserSearchSettingsFeedbackType = 'error'
          this.globalUserSearchSettingsFeedback =
            this.$t('newFeaturesGlobalUserSearch.saveError')
          console.log(error)
        } finally {
          this.isSavingGlobalUserSearchSettings = false
        }
      },
      async openMaintenanceSettings(item) {
        this.maintenanceFeature = item
        this.settingsDialog = true
        this.settingsFeedback = ''
        await this.loadMaintenanceSettings()
      },
      closeMaintenanceSettings() {
        this.settingsDialog = false
      },
      async openSupportChatSettings(item) {
        this.supportFeature = item
        this.supportSettingsDialog = true
        this.supportSettingsFeedback = ''
        await this.loadSupportChatSettings()
      },
      closeSupportChatSettings() {
        this.supportSettingsDialog = false
      },
      async loadMaintenanceSettings() {
        try {
          const doc = await db.collection('config').doc('maintenance').get()
          const data = doc.exists ? doc.data() || {} : {}
          const allowedRoles = Array.isArray(data.allowedRoles)
            ? data.allowedRoles
                .filter((v) => typeof v === 'string' && v.trim())
                .map((v) => String(v).trim())
            : []
          const allowedUids = Array.isArray(data.allowedUids)
            ? data.allowedUids.filter((v) => typeof v === 'string' && v.trim())
            : []
          this.selectedAllowedRoles = allowedRoles
          await this.loadAllowedUsers(allowedUids)
        } catch (error) {
          this.settingsFeedbackType = 'error'
          this.settingsFeedback =
            'Could not load maintenance settings. Please try again.'
          console.log(error)
        }
      },
      async loadAllowedUsers(uids) {
        const users = []
        for (const uid of uids) {
          try {
            const snap = await db
              .collection('users')
              .where('uid', '==', uid)
              .limit(1)
              .get()
            if (!snap.empty) {
              const user = snap.docs[0].data()
              users.push({
                uid,
                email: user.email || '',
              })
            } else {
              users.push({
                uid,
                email: '',
              })
            }
          } catch (error) {
            users.push({
              uid,
              email: '',
            })
          }
        }
        this.allowedUsers = users
      },
      async searchAndAddUser() {
        const email = String(this.emailSearch || '')
          .trim()
          .toLowerCase()
        if (!email) return

        this.settingsFeedback = ''
        this.isSearchingUser = true
        try {
          const snap = await db
            .collection('users')
            .where('email', '==', email)
            .limit(1)
            .get()
          if (snap.empty) {
            this.settingsFeedbackType = 'error'
            this.settingsFeedback = 'No user found for that email.'
            this.isSearchingUser = false
            return
          }

          const data = snap.docs[0].data() || {}
          const uid = data.uid
          if (!uid || typeof uid !== 'string') {
            this.settingsFeedbackType = 'error'
            this.settingsFeedback =
              'User was found but has no valid UID in Firestore.'
            this.isSearchingUser = false
            return
          }

          const alreadyExists = this.allowedUsers.some((u) => u.uid === uid)
          if (!alreadyExists) {
            this.allowedUsers.push({
              uid,
              email: data.email || email,
            })
            this.settingsFeedbackType = 'success'
            this.settingsFeedback = 'User added to maintenance allowlist.'
          } else {
            this.settingsFeedbackType = 'info'
            this.settingsFeedback = 'User is already in the allowlist.'
          }

          this.emailSearch = ''
        } catch (error) {
          this.settingsFeedbackType = 'error'
          this.settingsFeedback = 'User lookup failed. Please try again.'
          console.log(error)
        } finally {
          this.isSearchingUser = false
        }
      },
      removeAllowedUser(uid) {
        this.allowedUsers = this.allowedUsers.filter((u) => u.uid !== uid)
      },
      async saveMaintenanceSettings() {
        this.settingsFeedback = ''
        this.isSavingSettings = true
        try {
          const allowedUids = this.allowedUsers
            .map((u) => String(u.uid || '').trim())
            .filter(Boolean)
          const allowedRoles = this.selectedAllowedRoles
            .map((r) => String(r || '').trim())
            .filter(Boolean)
          const updatedBy =
            this.currentUser?.uid || this.currentUser?.email || 'unknown'

          await db.collection('config').doc('maintenance').set(
            {
              allowedUids,
              allowedRoles,
              updatedAt: Date.now(),
              updatedBy,
              // Keep legacy field for backwards compatibility / visibility.
              appMaintenance:
                this.maintenanceFeature &&
                this.maintenanceFeature.active === true,
            },
            { merge: true }
          )

          this.settingsFeedbackType = 'success'
          this.settingsFeedback = 'Maintenance settings saved.'
        } catch (error) {
          this.settingsFeedbackType = 'error'
          this.settingsFeedback = 'Could not save maintenance settings.'
          console.log(error)
        } finally {
          this.isSavingSettings = false
        }
      },
      async loadSupportChatSettings() {
        try {
          const doc = await db.collection('config').doc('supportChat').get()
          const data = doc.exists ? doc.data() || {} : {}
          const allowed = [
            'none',
            'lhc',
            'zendesk',
            'lifeOnLifeSupport',
          ]
          this.supportProvider = allowed.includes(data.provider)
            ? data.provider
            : 'lhc'

          const lhc = data.lhc && typeof data.lhc === 'object' ? data.lhc : {}
          const zendesk =
            data.zendesk && typeof data.zendesk === 'object' ? data.zendesk : {}
          const lol =
            data.lifeOnLifeSupport &&
            typeof data.lifeOnLifeSupport === 'object'
              ? data.lifeOnLifeSupport
              : {}

          this.lhcBasePath = lhc.lhcBasePath || '/lhc/lhc_web/index.php/'
          this.lhcWidgetScriptPath =
            lhc.widgetScriptPath ||
            '/lhc/lhc_web/design/defaulttheme/js/widgetv2/index.js'
          this.lhcFallbackStartUrl =
            lhc.fallbackStartUrl ||
            'https://support.lifeonlife.org/lhc/lhc_web/index.php/en/chat/start'

          this.zendeskKey =
            typeof zendesk.key === 'string' ? zendesk.key.trim() : ''
          this.zendeskLocale =
            typeof zendesk.locale === 'string' ? zendesk.locale.trim() : ''

          const url =
            typeof lol.ticketUrl === 'string' ? lol.ticketUrl.trim() : ''
          this.lifeOnLifeTicketUrl = url || DEFAULT_LIFE_ON_LIFE_TICKET_URL
        } catch (error) {
          this.supportSettingsFeedbackType = 'error'
          this.supportSettingsFeedback =
            'Could not load support chat settings. Please try again.'
          console.log(error)
        }
      },
      async saveSupportChatSettings() {
        this.supportSettingsFeedback = ''
        this.isSavingSupportSettings = true
        try {
          const updatedBy =
            this.currentUser?.uid || this.currentUser?.email || 'unknown'
          const provider = [
            'none',
            'lhc',
            'zendesk',
            'lifeOnLifeSupport',
          ].includes(this.supportProvider)
            ? this.supportProvider
            : 'lhc'

          const ticketRaw = String(this.lifeOnLifeTicketUrl || '').trim()
          await db.collection('config').doc('supportChat').set(
            {
              provider,
              lhc: {
                lhcBasePath: String(this.lhcBasePath || '').trim(),
                widgetScriptPath: String(this.lhcWidgetScriptPath || '').trim(),
                fallbackStartUrl: String(this.lhcFallbackStartUrl || '').trim(),
              },
              zendesk: {
                key: String(this.zendeskKey || '').trim(),
                locale: String(this.zendeskLocale || '').trim(),
              },
              lifeOnLifeSupport: {
                ticketUrl:
                  ticketRaw ||
                  DEFAULT_LIFE_ON_LIFE_TICKET_URL,
              },
              updatedAt: Date.now(),
              updatedBy,
              // Keep feature-level status visible in config for audits.
              featureActive:
                this.supportFeature && this.supportFeature.active === true,
            },
            { merge: true }
          )

          await this.$store.dispatch('AppData/getSupportChatConfig')
          this.supportSettingsFeedbackType = 'success'
          this.supportSettingsFeedback = 'Support chat settings saved.'
        } catch (error) {
          this.supportSettingsFeedbackType = 'error'
          this.supportSettingsFeedback = 'Could not save support chat settings.'
          console.log(error)
        } finally {
          this.isSavingSupportSettings = false
        }
      },
      getNewFeatures() {
        this.features = []
        let newFeaturesRef = db.collection('newfeatures')
        newFeaturesRef.get().then((snapshot) => {
          snapshot.forEach((doc) => {
            let features = doc.data()
            features.id = doc.id
            this.features.push(features)
          })
          // console.log(this.content)
        })
      },
      imageURL(file) {
        console.log(file)
      },
    },
    created() {
      this.getNewFeatures()
    },
  }
</script>

<style scoped>
  .section-heading h1 {
    display: flex;
    align-items: center;
  }
  .jrnyorient-icon {
    margin-right: 10px;
  }
  #newfeatures .grid-row {
    gap: 30px;
  }
  #newfeatures .grid-row > div {
    flex: auto;
    display: flex;
    flex-direction: column;
  }
  #newfeatures .grid-row > div:last-child {
    margin-right: 0;
  }
  .maintenance-settings-btn {
    text-transform: none;
    min-width: 0;
    font-weight: 600;
  }
  .maintenance-save-btn {
    background-color: #77b718;
    color: #ffffff;
    min-width: 92px;
    font-weight: 700;
    text-transform: none;
  }

  /** Support Chat dialog: fixed header/footer, scroll provider fields only */
  .support-settings-dialog-card {
    display: flex;
    flex-direction: column;
    max-height: min(88vh, 900px);
    overflow: hidden;
  }

  .support-settings-dialog-title {
    flex-shrink: 0;
  }

  .support-settings-dialog-header {
    flex-shrink: 0;
    padding-inline: 24px;
    padding-block: 0 8px;
  }

  .support-settings-dialog-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .support-settings-dialog-footer {
    flex-shrink: 0;
    padding-inline: 24px !important;
    padding-block: 12px !important;
  }
</style>
